import { useEffect, useSyncExternalStore } from 'react';
import { ApiError } from '../../sync/api';
import { getBoard, type BoardSummary } from '../../sync/boardsApi';

/**
 * Live facts about boards referenced by board cards (title, dashboard preview, access), fetched
 * once per board and shared by every card that points at it. A lookup that fails with 404 means
 * the visitor cannot see that board (or it is gone): the card then shows its custom title only.
 */

export interface BoardLookup {
  status: 'loading' | 'ok' | 'missing' | 'error';
  board: BoardSummary | null;
  fetchedAt: number;
}

const STALE_MS = 60_000;
const LOADING: BoardLookup = { status: 'loading', board: null, fetchedAt: 0 };

const cache = new Map<string, BoardLookup>();
const inflight = new Map<string, Promise<BoardLookup>>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

/** Seeds the cache from a list the app already holds (the board picker), so new cards render at once. */
export function primeBoardLookup(board: BoardSummary): void {
  cache.set(board.id, { status: 'ok', board, fetchedAt: Date.now() });
  notify();
}

export function fetchBoardLookup(boardId: string, force = false): Promise<BoardLookup> {
  const cached = cache.get(boardId);
  if (!force && cached && cached.status !== 'loading' && Date.now() - cached.fetchedAt < STALE_MS) return Promise.resolve(cached);
  let p = inflight.get(boardId);
  if (p) return p;
  p = getBoard(boardId)
    .then((board): BoardLookup => ({ status: 'ok', board, fetchedAt: Date.now() }))
    .catch((e: unknown): BoardLookup => {
      const missing = e instanceof ApiError && (e.status === 404 || e.status === 403 || e.status === 401);
      // keep what we knew on a transient failure, so a flaky connection does not blank the cards
      const prev = cache.get(boardId);
      if (!missing && prev?.status === 'ok') return { ...prev, fetchedAt: Date.now() };
      return { status: missing ? 'missing' : 'error', board: null, fetchedAt: Date.now() };
    })
    .then((result) => {
      cache.set(boardId, result);
      inflight.delete(boardId);
      notify();
      return result;
    });
  inflight.set(boardId, p);
  return p;
}

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

/** The lookup for a board, refreshed on mount and when the tab comes back, once older than a minute. */
export function useBoardLookup(boardId: string): BoardLookup {
  const lookup = useSyncExternalStore(subscribe, () => cache.get(boardId) ?? LOADING);
  useEffect(() => {
    if (!boardId) return;
    void fetchBoardLookup(boardId);
    const onVisible = () => {
      if (!document.hidden) void fetchBoardLookup(boardId);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [boardId]);
  return lookup;
}
